import React from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { House, CaretRight } from '@phosphor-icons/react';

export const Breadcrumb = () => {
  const { country, region, subregion, grapeVariety } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show breadcrumb on home page
  if (!country && !location.pathname.startsWith('/info')) return null;

  const breadcrumbItems = [
    {
      label: 'Accueil',
      icon: <House size={16} />,
      url: '/',
      isLink: true
    }
  ];

  // Handle new URL structure: /info/countries/...
  if (location.pathname.startsWith('/info/countries')) {
    breadcrumbItems.push({
      label: 'Guide du Vin',
      url: '/info',
      isLink: true
    });

    breadcrumbItems.push({
      label: 'Pays Viticoles',
      url: '/info/countries',
      isLink: true
    });

    // Add country level
    if (country) {
      breadcrumbItems.push({
        label: getCountryDisplayName(country),
        url: `/info/countries/${country}`,
        isLink: true
      });
    }

    // Add region level
    if (region && region !== country) {
      breadcrumbItems.push({
        label: getRegionDisplayName(region),
        url: `/info/countries/${country}/regions/${region}`,
        isLink: true
      });
    }

    // Add subregion level
    if (subregion && subregion !== region) {
      breadcrumbItems.push({
        label: getSubregionDisplayName(subregion),
        url: `/info/countries/${country}/regions/${region}/${subregion}`,
        isLink: true
      });
    }

    // Add cepages level
    if (location.pathname.includes('/cepages') && !grapeVariety) {
      breadcrumbItems.push({
        label: 'Cépages',
        url: null, // Current page
        isLink: false
      });
    }

    // Add specific grape variety level
    if (grapeVariety) {
      breadcrumbItems.push({
        label: 'Cépages',
        url: subregion 
          ? `/info/countries/${country}/regions/${region}/${subregion}/cepages`
          : `/info/countries/${country}/regions/${region}/cepages`,
        isLink: true
      });

      breadcrumbItems.push({
        label: getGrapeVarietyDisplayName(grapeVariety),
        url: null, // Current page
        isLink: false
      });
    }
  }
  // Handle legacy URL structure: /info/...
  else if (location.pathname.startsWith('/info') && country) {
    breadcrumbItems.push({
      label: 'Guide du Vin',
      url: '/info',
      isLink: true
    });

    // Add country level
    breadcrumbItems.push({
      label: getCountryDisplayName(country),
      url: `/info/${country}`,
      isLink: true
    });

    // Add region level
    if (region && region !== country) {
      breadcrumbItems.push({
        label: getRegionDisplayName(region),
        url: `/info/${country}/regions/${region}`,
        isLink: true
      });
    }

    // Add subregion level
    if (subregion && subregion !== region) {
      breadcrumbItems.push({
        label: getSubregionDisplayName(subregion),
        url: `/info/${country}/regions/${region}/${subregion}`,
        isLink: true
      });
    }

    // Add cepages level
    if (location.pathname.includes('/cepages') && !grapeVariety) {
      breadcrumbItems.push({
        label: 'Cépages',
        url: null, // Current page
        isLink: false
      });
    }

    // Add specific grape variety level
    if (grapeVariety) {
      breadcrumbItems.push({
        label: 'Cépages',
        url: subregion 
          ? `/info/${country}/regions/${region}/${subregion}/cepages`
          : `/info/${country}/regions/${region}/cepages`,
        isLink: true
      });

      breadcrumbItems.push({
        label: getGrapeVarietyDisplayName(grapeVariety),
        url: null, // Current page
        isLink: false
      });
    }
  }
  // Handle info overview pages
  else if (location.pathname === '/info') {
    breadcrumbItems.push({
      label: 'Guide du Vin',
      url: null, // Current page
      isLink: false
    });
  }
  else if (location.pathname === '/info/countries') {
    breadcrumbItems.push({
      label: 'Guide du Vin',
      url: '/info',
      isLink: true
    });

    breadcrumbItems.push({
      label: 'Pays Viticoles',
      url: null, // Current page
      isLink: false
    });
  }

  // Helper functions to get display names
  function getCountryDisplayName(countrySlug) {
    const countryMap = {
      'france': 'France',
      'spain': 'Espagne',
      'italy': 'Italie',
      'germany': 'Allemagne',
      'australia': 'Australie',
      'new-zealand': 'Nouvelle-Zélande',
      'california': 'Californie',
      'chile': 'Chili',
      'argentina': 'Argentine',
      'south-africa': 'Afrique du Sud',
      'portugal': 'Portugal'
    };
    return countryMap[countrySlug] || countrySlug;
  }

  function getRegionDisplayName(regionSlug) {
    const regionMap = {
      'bordeaux': 'Bordeaux',
      'provence': 'Provence',
      'bourgogne': 'Bourgogne',
      'champagne': 'Champagne',
      'loire': 'Vallée de la Loire',
      'rhone': 'Vallée du Rhône',
      'alsace': 'Alsace',
      'languedoc-roussillon': 'Languedoc-Roussillon',
      'sud-ouest': 'Sud-Ouest',
      'rioja': 'Rioja',
      'ribera-del-duero': 'Ribera del Duero',
      'navarra': 'Navarra',
      'priorat': 'Priorat',
      'rueda': 'Rueda',
      'rias-baixas': 'Rías Baixas',
      'jerez': 'Jerez',
      'cava': 'Cava',
      'douro': 'Douro',
      'alentejo': 'Alentejo',
      'dao': 'Dão',
      'vinho-verde': 'Vinho Verde',
      'bairrada': 'Bairrada',
      'lisboa': 'Lisboa',
      'setubal': 'Setúbal',
      'madeira': 'Madeira',
      'toscane': 'Toscane',
      'piemont': 'Piémont',
      'venetie': 'Vénétie',
      'sicile': 'Sicile',
      'supertuscan': 'SuperTuscan'
    };
    return regionMap[regionSlug] || regionSlug;
  }

  function getSubregionDisplayName(subregionSlug) {
    const subregionMap = {
      'listrac-medoc': 'Listrac-Médoc',
      'moulis-en-medoc': 'Moulis-en-Médoc',
      'margaux': 'Margaux',
      'pauillac': 'Pauillac',
      'saint-julien': 'Saint-Julien',
      'saint-estephe': 'Saint-Estèphe',
      'saint-emilion': 'Saint-Émilion',
      'pomerol': 'Pomerol',
      'graves': 'Graves',
      'sauternes': 'Sauternes',
      'barsac': 'Barsac',
      'bandol': 'Bandol',
      'cotes-provence': 'Côtes de Provence',
      'cassis': 'Cassis',
      'chateauneuf-du-pape': 'Châteauneuf-du-Pape',
      'cote-rotie': 'Côte-Rôtie',
      'hermitage': 'Hermitage',
      'sancerre': 'Sancerre',
      'pouilly-fume': 'Pouilly-Fumé',
      'rioja-alta': 'Rioja Alta',
      'rioja-alavesa': 'Rioja Alavesa',
      'rioja-baja': 'Rioja Baja',
      'ribera-alta': 'Ribera Alta',
      'ribera-media': 'Ribera Media',
      'ribera-baja': 'Ribera Baja',
      'tierra-estella': 'Tierra Estella',
      'valdizarbe': 'Valdizarbe',
      'baja-montana': 'Baja Montaña',
      'cima-corgo': 'Cima Corgo',
      'alentejo-central': 'Alentejo Central',
      'serra-da-estrela': 'Serra da Estrela',
      'moncao-melgaco': 'Monção e Melgaço',
      'chianti-classico': 'Chianti Classico',
      'langhe': 'Langhe',
      'valpolicella': 'Valpolicella',
      'bolgheri': 'Bolgheri',
      'maremma': 'Maremma'
    };
    return subregionMap[subregionSlug] || subregionSlug;
  }

  function getGrapeVarietyDisplayName(grapeVarietySlug) {
    const grapeVarietyMap = {
      'chardonnay': 'Chardonnay',
      'cabernet-sauvignon': 'Cabernet Sauvignon',
      'merlot': 'Merlot',
      'pinot-noir': 'Pinot Noir',
      'syrah': 'Syrah',
      'sauvignon-blanc': 'Sauvignon Blanc',
      'grenache': 'Grenache',
      'mourvedre': 'Mourvèdre',
      'cinsault': 'Cinsault',
      'carignan': 'Carignan',
      'petit-verdot': 'Petit Verdot',
      'tempranillo': 'Tempranillo',
      'graciano': 'Graciano',
      'garnacha': 'Garnacha',
      'verdejo': 'Verdejo',
      'albariño': 'Albariño',
      'touriga-nacional': 'Touriga Nacional',
      'alvarinho': 'Alvarinho',
      'baga': 'Baga',
      'encruzado': 'Encruzado',
      'sangiovese': 'Sangiovese',
      'nebbiolo': 'Nebbiolo',
      'pinot-grigio': 'Pinot Grigio',
      'nero-davola': 'Nero d\'Avola',
      'cabernet-franc': 'Cabernet Franc'
    };
    return grapeVarietyMap[grapeVarietySlug] || grapeVarietySlug;
  }

  return (
    <nav 
      style={{ 
        padding: '16px 20px', 
        background: '#f8f6fa', 
        borderBottom: '1px solid #e8e0e8',
        marginBottom: '20px'
      }}
      aria-label="Breadcrumb"
    >
      <ol style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        margin: 0, 
        padding: 0, 
        listStyle: 'none',
        fontSize: '14px',
        color: '#666'
      }}>
        {breadcrumbItems.map((item, index) => (
          <li key={index} style={{ display: 'flex', alignItems: 'center' }}>
            {index > 0 && (
              <CaretRight size={16} color="#999" style={{ margin: '0 4px' }} />
            )}
            
            {item.isLink ? (
              <Link
                to={item.url}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#4B164C',
                  textDecoration: 'none',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background 0.2s',
                  fontWeight: '500'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                {item.icon}
                {item.label}
              </Link>
            ) : (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#333',
                fontWeight: '600',
                padding: '4px 8px'
              }}>
                {item.icon}
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}; 